import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { BLOG_POSTS, getPostBySlug } from "@/lib/blog-posts";
import { buildMeta } from "@/lib/seo";
import { Calendar, Clock, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return { meta: [] };
    const base = buildMeta({
      // Suffix " | Tanie Latanie" pominięty, by zmieścić tytuł w 60 znakach (limit SERP).
      title: post.title,
      description: post.description,
      path: `/blog/${post.slug}`,
      type: "article",
      publishedTime: post.publishedAt,
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Strona główna", item: "https://tanielatanie.pl/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://tanielatanie.pl/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: `https://tanielatanie.pl/blog/${post.slug}` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.publishedAt,
            dateModified: post.updatedAt ?? post.publishedAt,
            inLanguage: "pl-PL",
            articleSection: post.category,
            wordCount: post.body.split(/\s+/).length,
            author: { "@type": "Organization", name: "Tanie Latanie", url: "https://tanielatanie.pl" },
            publisher: {
              "@type": "Organization",
              name: "Tanie Latanie",
              logo: { "@type": "ImageObject", url: "https://tanielatanie.pl/apple-touch-icon.png" },
            },
            image: "https://tanielatanie.pl/og-default.jpg",
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://tanielatanie.pl/blog/${post.slug}` },
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "header p", "article p"],
            },
          }),
        },
        ...(post.faq && post.faq.length > 0
          ? [{
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: post.faq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen">
      <Header />
      <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Nie znaleziono artykułu</h1>
        <Link to="/blog" className="mt-6 inline-flex text-primary hover:underline">← Wróć do bloga</Link>
      </div>
      <Footer />
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen">
      <Header />
      <article className="container mx-auto max-w-3xl px-4 py-10">
        <Link to="/blog" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Wszystkie artykuły
        </Link>

        <header className="mb-8">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="rounded-full bg-primary/15 px-2.5 py-0.5 font-medium text-primary">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(post.publishedAt).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readingTime}
            </span>
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight md:text-5xl">{post.title}</h1>
          <p className="mt-3 text-lg text-muted-foreground">{post.description}</p>
        </header>

        <div className="prose prose-invert max-w-none prose-headings:font-display prose-headings:font-bold prose-h2:mt-8 prose-h2:text-2xl prose-h3:mt-6 prose-h3:text-xl prose-p:leading-relaxed prose-li:my-1">
          {renderMarkdown(post.body)}
        </div>

        {post.sources && post.sources.length > 0 && (
          <section className="mt-8 border-t border-border pt-6">
            <h2 className="font-display text-xl font-bold">Źródła i aktualizacja</h2>
            {post.updatedAt && <p className="mt-2 text-sm text-muted-foreground">Aktualizacja: {new Date(post.updatedAt).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}</p>}
            <ul className="mt-3 space-y-2 text-sm">
              {post.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">{source.label}</a></li>)}
            </ul>
          </section>
        )}
        <nav className="mt-8 flex flex-wrap gap-x-5 gap-y-3 text-sm" aria-label="Planuj podróż">
          <Link to="/" className="text-primary hover:underline">Tanie Latanie — poradnik</Link>
          <Link to="/tanie-loty" className="text-primary hover:underline">Wyszukaj tanie loty</Link>
          <Link to="/loty" className="text-primary hover:underline">Loty z Polski</Link>
          <Link to="/okazje" className="text-primary hover:underline">Sprawdź okazje</Link>
        </nav>

        {post.faq && post.faq.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 font-display text-2xl font-bold">FAQ</h2>
            <div className="space-y-3">
              {post.faq.map((f: { q: string; a: string }, i: number) => (
                <details key={i} className="glass rounded-lg p-4">
                  <summary className="cursor-pointer font-semibold">{f.q}</summary>
                  <p className="mt-2 text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 font-display text-2xl font-bold">Może Cię zainteresować</h2>
            <div className="grid gap-3">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="block rounded-lg border border-border/50 bg-card/40 px-4 py-3 transition hover:border-primary/40 hover:bg-card/70"
                >
                  <span className="font-semibold">{p.title}</span>
                  <span className="block text-sm text-muted-foreground">{p.description}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
      <Footer />
    </div>
  );
}

// Minimal markdown renderer (## headings, lists, paragraphs, **bold**)
function renderMarkdown(md: string) {
  const blocks = md.trim().split(/\n\n+/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return <h2 key={i}>{block.slice(3)}</h2>;
    }
    if (block.startsWith("### ")) {
      return <h3 key={i}>{block.slice(4)}</h3>;
    }
    if (/^[-*]\s/.test(block)) {
      const items = block.split("\n").map((l) => l.replace(/^[-*]\s/, ""));
      return (
        <ul key={i}>
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    if (/^\d+\.\s/.test(block)) {
      const items = block.split("\n").map((l) => l.replace(/^\d+\.\s/, ""));
      return (
        <ol key={i}>
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ol>
      );
    }
    return <p key={i}>{renderInline(block)}</p>;
  });
}

function renderInline(text: string) {
  // simple **bold** support
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
